import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { TrialStatus } from '@/app/external/types';
import type { PatientDownloadableAtoms } from '@/modules/annotator/casePresentation/patientDownloadable/hooks/usePatientDownloadableSettings';
import type { VisitIdToImageIdsMap } from '@/modules/annotator/casePresentation/patientDownloadable/hooks/usePatientPrintoutData';
import type { PracticeFeatureFlagKey } from '@/utils/user';

import type { GetPatientLatestImagesResponseV2 } from '../@types/latestImagesV2';
import { makeAPIRequestV2 } from '../utils/api';

import { endpoints } from './endpoints';

export interface PublicPatientInfo {
  practiceName: string;
  practiceId: string;
  patientId: string;
  organizationId: string;
  organizationName: string;
}

export interface GetPublicPatientDownloadableDataResponse {
  latestImages: GetPatientLatestImagesResponseV2[];
  patientInfo: PublicPatientInfo;
  salesDemoPracticeConfig?: {
    practiceId: string;
    practiceName: string;
  };
  practiceFeatureFlags: {
    [key in PracticeFeatureFlagKey]?: boolean;
  };
  atoms: PatientDownloadableAtoms;
  rblTemplateName?: string;
  visitIdToImageIds: VisitIdToImageIdsMap;
  practiceCountry?: string;

  // External patient data
  isIframe: boolean;
  externalPatientId: string | null;
  externalImageId: string | null;

  reverseTrialData?: {
    trialStatus?: TrialStatus;
    centralizedBilling?: boolean;
  };
}

// API function to fetch data and validate token
const fetchPublicPatientDownloadableData = async ({
  shortCode,
}: {
  shortCode: string;
}): Promise<GetPublicPatientDownloadableDataResponse> => {
  const url = `/${endpoints.v2}?shortCode=${shortCode}`;

  const response = await makeAPIRequestV2(url, {}, 'GET');
  return response.data;
};

export const usePublicPatientDownloadable = ({
  shortCode,
  ...options
}: {
  shortCode: string;
} & UseQueryOptions<GetPublicPatientDownloadableDataResponse>) => {
  return useQuery<GetPublicPatientDownloadableDataResponse>(
    [`${endpoints.external}/${endpoints.patientDownloadable}`, shortCode],
    () =>
      fetchPublicPatientDownloadableData({
        shortCode,
      }),
    {
      enabled: !!shortCode,
      retry: 0,
      retryDelay: 2000,
      ...options,
    },
  );
};
